import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './query-keys';
import type {
  ArticleCollectionWithItems,
  CreateArticleCollectionRequest,
  UpdateArticleCollectionRequest,
  AddCollectionItemRequest,
  UpdateCollectionItemRequest,
  ReorderCollectionItemsRequest,
  BulkAddArticlesRequest,
} from '@/types/article-collection';

// ============================================================================
// Query Options
// ============================================================================

// Query options for a single collection with items
export const articleCollectionQueryOptions = (id: string) =>
  queryOptions({
    queryKey: queryKeys.articleCollections.detail(id),
    queryFn: async () => {
      const response = await api.getArticleCollection(id);
      return response.collection;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

// Query options for the collection tree (for reading sidebar)
export const collectionTreeQueryOptions = (collectionId: string) =>
  queryOptions({
    queryKey: queryKeys.articleCollections.tree(collectionId),
    queryFn: async () => {
      const response = await api.getCollectionTree(collectionId);
      return response.collection;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

// ============================================================================
// Query Hooks
// ============================================================================

// Hook to fetch all collections
export function useArticleCollections() {
  return useQuery({
    queryKey: queryKeys.articleCollections.lists(),
    queryFn: async () => {
      const response = await api.getArticleCollections();
      return response.collections;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a single collection with its items
export function useArticleCollection(id: string) {
  return useQuery(articleCollectionQueryOptions(id));
}

// Hook to fetch collection tree for reading mode
export function useCollectionTree(collectionId: string) {
  return useQuery(collectionTreeQueryOptions(collectionId));
}

// ============================================================================
// Collection Mutation Hooks
// ============================================================================

// Hook to create a new collection
export function useCreateArticleCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateArticleCollectionRequest) =>
      api.createArticleCollection(data),
    onSuccess: () => {
      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.lists(),
      });
    },
  });
}

// Hook to update a collection
export function useUpdateArticleCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateArticleCollectionRequest }) =>
      api.updateArticleCollection(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.articleCollections.detail(id),
      });

      const previous = queryClient.getQueryData<ArticleCollectionWithItems>(
        queryKeys.articleCollections.detail(id)
      );

      if (previous) {
        queryClient.setQueryData<ArticleCollectionWithItems>(
          queryKeys.articleCollections.detail(id),
          { ...previous, ...data }
        );
      }

      return { previous, id };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.articleCollections.detail(context.id),
          context.previous
        );
      }
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.lists(),
      });
    },
  });
}

// Hook to delete a collection
export function useDeleteArticleCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteArticleCollection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.lists(),
      });
    },
  });
}

// ============================================================================
// Collection Item Mutation Hooks
// ============================================================================

// Hook to add an item (article or heading) to a collection
export function useAddCollectionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: AddCollectionItemRequest;
    }) => api.addCollectionItem(collectionId, data),
    onSuccess: (_, { collectionId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.detail(collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.tree(collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.lists(),
      });
    },
  });
}

// Hook to update a collection item
export function useUpdateCollectionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      itemId,
      data,
    }: {
      collectionId: string;
      itemId: string;
      data: UpdateCollectionItemRequest;
    }) => api.updateCollectionItem(collectionId, itemId, data),
    onSuccess: (_, { collectionId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.detail(collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.tree(collectionId),
      });
    },
  });
}

// Hook to delete a collection item
export function useDeleteCollectionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      itemId,
    }: {
      collectionId: string;
      itemId: string;
    }) => api.deleteCollectionItem(collectionId, itemId),
    onSuccess: (_, { collectionId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.detail(collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.tree(collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.lists(),
      });
    },
  });
}

// Hook to reorder items within a collection
export function useReorderCollectionItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: ReorderCollectionItemsRequest;
    }) => api.reorderCollectionItems(collectionId, data),
    onSuccess: (_, { collectionId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.detail(collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.tree(collectionId),
      });
    },
  });
}

// Hook to bulk add articles to a collection
export function useBulkAddArticles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: BulkAddArticlesRequest;
    }) => api.bulkAddArticlesToCollection(collectionId, data),
    onSuccess: (_, { collectionId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.detail(collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.tree(collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articleCollections.lists(),
      });
    },
  });
}
